import type { MarketOrderSuggestionItem, MarketOrderSuggestionList, PurchaseOrder } from "@station/contracts";
import { newIdempotencyKey } from "@station/api-client";
import { Decimal, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DateInput,
  EmptyState,
  FormField,
  Input,
  MoneyText,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardList, ListPlus, PackagePlus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CounterpartyIdCombobox } from "../../../features/counterparties/counterparty-combobox.js";
import { Th } from "../../../features/fuel/shared.js";
import { formatStockQuantity } from "../../../features/market/market-values.js";
import { useOrderSuggestions } from "../../../features/market/purchasing-queries.js";
import {
  suggestionBlocker,
  suggestionPicks,
  suggestionQuantityText,
} from "../../../features/market/purchasing-values.js";
import { useMarketPermissions, useRefreshMarket } from "../../../features/market/queries.js";
import {
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  PurchaseOrderStatusBadge,
  TableFrame,
  useMarketStations,
} from "../../../features/market/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate, formatUnitPrice } from "../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  supplier: z.string().uuid().optional().catch(undefined),
  all: z.enum(["true"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/market/siparis-onerisi")({
  validateSearch: (search) => searchSchema.parse(search),
  component: OrderSuggestionsRoute,
});

/**
 * Market sipariş önerisi (Prompt 16 — web, aşama B; ADR 0016 §15). İstasyon (+ tedarikçi) seçilir; öneri min / max
 * stok, satış hızı, teslim süresi ve koli yuvarlamasıyla SUNUCUDA hesaplanır. Seçilen satırlardan tedarikçi başına
 * TASLAK sipariş açılır (`from-suggestions`, düzenlenen miktarlarla — `items`). Tedarikçisi ya da liste
 * fiyatı olmayan satır dönüştürülemez (satırda nedeni yazar). Okuma `market.read`, sipariş `market.purchase.manage`.
 */
function OrderSuggestionsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketOrderSuggestions")}
      description={t("market.suggestions.description")}
      breadcrumb={[{ label: t("menu.marketOrderSuggestions") }]}
    >
      {({ companyId }) => <SuggestionsBody companyId={companyId} />}
    </MarketPage>
  );
}

function SuggestionsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useMarketStations(companyId, { required: true });
  const includeAll = search.all === "true";
  const suggestions = useOrderSuggestions(station.stationId, { supplierId: search.supplier, includeAll });

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} allowAll={false} />
        <FormField label={t("market.suggestions.supplier")} className="w-72">
          {(control) => (
            <CounterpartyIdCombobox
              {...control}
              companyId={companyId}
              counterpartyId={search.supplier ?? null}
              placeholder={t("market.suggestions.allSuppliers")}
              onChange={(supplierId) => setSearch({ supplier: supplierId ?? undefined })}
            />
          )}
        </FormField>
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <Checkbox
            checked={includeAll}
            onChange={(event) => setSearch({ all: event.target.checked ? "true" : undefined })}
          />
          {t("market.suggestions.includeAll")}
        </label>
        <Button variant="outline" className="ms-auto" asChild>
          <Link to="/market/siparisler" search={{ station: station.stationId }}>
            <ClipboardList aria-hidden="true" />
            {t("menu.marketPurchaseOrders")}
          </Link>
        </Button>
      </Card>

      {suggestions.error ? (
        <Alert tone="danger">{errorMessage(suggestions.error)}</Alert>
      ) : !suggestions.data ? (
        <SkeletonRows rows={6} />
      ) : (
        // Seçim ve düzenlenen miktarlar istasyon / tedarikçi / süzgeç değişince sıfırlanır.
        <SuggestionTable
          key={`${station.stationId}:${search.supplier ?? ""}:${includeAll}`}
          list={suggestions.data}
          stationId={station.stationId}
          supplierId={search.supplier ?? null}
        />
      )}
    </>
  );
}

function SuggestionTable({
  list,
  stationId,
  supplierId,
}: {
  list: MarketOrderSuggestionList;
  stationId: string;
  supplierId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMarket();
  const permissions = useMarketPermissions();
  const canOrder = permissions.canManagePurchases;
  const today = toIstanbulDate(new Date());
  // Başlangıçta önerisi olan ve dönüştürülebilen satırlar seçilidir.
  const [selected, setSelected] = useState<Set<string>>(
    () =>
      new Set(
        list.items
          .filter((item) => suggestionBlocker(item) === null && suggestionQuantityText(item) !== "")
          .map((item) => item.productId),
      ),
  );
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [expectedDate, setExpectedDate] = useState<LocalDate | null>(null);
  const [note, setNote] = useState("");
  const [showProblems, setShowProblems] = useState(false);
  const [result, setResult] = useState<{
    orders: PurchaseOrder[];
    skipped: Array<{ productId: string; productName: string; reason: string }>;
  } | null>(null);
  const { picks, problems } = suggestionPicks(list.items, selected, quantities);

  const toggle = (productId: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(productId);
      else next.delete(productId);
      return next;
    });

  const create = useMutation({
    mutationFn: async () => {
      const trimmed = note.trim();
      // Düzenlenen miktarlar TEK çağrıda gider (`items`): sunucu önerisi sıfır olsa da seçilen miktarla sipariş açar ve
      // öneriyi `suggestedQuantity` izi olarak saklar — istemci tarafı uzlaştırma (PUT / POST) gerekmez.
      const created = (
        await api.POST("/market/purchase-orders/from-suggestions", {
          params: { header: { "Idempotency-Key": newIdempotencyKey() } },
          body: {
            stationId,
            ...(supplierId ? { supplierId } : {}),
            items: picks.map((pick) => ({ productId: pick.productId, quantity: pick.quantity })),
            expectedDate,
            note: trimmed === "" ? null : trimmed,
          },
        })
      ).data!;
      return { orders: created.orders, skipped: created.skipped };
    },
    onSuccess: async (outcome) => {
      setResult(outcome);
      setShowProblems(false);
      await refresh();
      toast({
        tone: outcome.orders.length > 0 ? "success" : "info",
        title:
          outcome.orders.length > 0
            ? t("market.suggestions.created", { count: outcome.orders.length })
            : t("market.suggestions.nothingCreated"),
      });
    },
    onError: (caught) => toast({ tone: "danger", title: errorMessage(caught) }),
  });

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {t("market.suggestions.basis", {
          days: list.salesVelocityDays,
          date: formatDate(list.businessDate),
        })}
      </p>

      {list.suppliers.length > 0 ? (
        <ul
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          aria-label={t("market.suggestions.bySupplier")}
        >
          {list.suppliers.map((supplier) => (
            <li key={supplier.supplierId ?? "none"}>
              <Card className="grid gap-1 p-4">
                <span className="font-medium">
                  {supplier.supplierName ?? t("market.suggestions.noSupplierGroup")}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t("market.suggestions.supplierLines", { count: supplier.lineCount })}
                </span>
                <MoneyText value={supplier.estimatedAmount} className="text-lg font-semibold" />
              </Card>
            </li>
          ))}
        </ul>
      ) : null}

      {list.items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<ListPlus />}
          title={t("market.suggestions.emptyTitle")}
          description={t("market.suggestions.emptyDescription")}
        />
      ) : (
        <TableFrame label={t("market.suggestions.caption")}>
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr>
              <Th>
                <span className="sr-only">{t("market.suggestions.select")}</span>
              </Th>
              <Th>{t("market.fields.product")}</Th>
              <Th align="right">{t("market.fields.onHand")}</Th>
              <Th align="right">{t("market.suggestions.fields.openOrder")}</Th>
              <Th align="right">{t("market.suggestions.fields.pendingOrder")}</Th>
              <Th align="right">{t("market.suggestions.fields.dailySales")}</Th>
              <Th align="right">{t("market.suggestions.fields.projected")}</Th>
              <Th align="right">{t("market.fields.minMax")}</Th>
              <Th align="right">{t("market.suggestions.fields.suggested")}</Th>
              <Th align="right">{t("market.suggestions.fields.packSize")}</Th>
              <Th>{t("market.suggestions.fields.supplier")}</Th>
              <Th align="right">{t("market.suggestions.fields.estimated")}</Th>
            </tr>
          </thead>
          <tbody>
            {list.items.map((item) => (
              <SuggestionRow
                key={item.productId}
                item={item}
                checked={selected.has(item.productId)}
                quantity={quantities[item.productId] ?? suggestionQuantityText(item)}
                problem={showProblems ? (problems.get(item.productId) ?? null) : null}
                canOrder={canOrder}
                onToggle={(checked) => toggle(item.productId, checked)}
                onQuantity={(text) => {
                  setQuantities((current) => ({ ...current, [item.productId]: text }));
                  // Önerisi sıfır satıra miktar yazmak onu seçer (kullanıcı sipariş etmek istiyor).
                  if (text.trim() !== "" && suggestionBlocker(item) === null) toggle(item.productId, true);
                }}
              />
            ))}
          </tbody>
        </TableFrame>
      )}

      {canOrder && list.items.length > 0 ? (
        <Card className="grid gap-4 p-4">
          <div className="flex flex-wrap items-end gap-3">
            <FormField
              label={t("market.orders.fields.expectedDate")}
              description={t("market.suggestions.expectedDateHint")}
              className="w-48"
            >
              {(control) => (
                <DateInput {...control} value={expectedDate} min={today} onValueChange={setExpectedDate} />
              )}
            </FormField>
            <FormField label={t("market.fields.note")} className="min-w-64 flex-1">
              {(control) => (
                <Input
                  {...control}
                  maxLength={1000}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              )}
            </FormField>
            <Button
              loading={create.isPending}
              disabled={selected.size === 0}
              onClick={() => {
                setShowProblems(true);
                if (problems.size === 0 && picks.length > 0) create.mutate();
              }}
            >
              <PackagePlus aria-hidden="true" />
              {t("market.suggestions.createOrders", { count: selected.size })}
            </Button>
          </div>
          {showProblems && problems.size > 0 ? (
            <Alert tone="warning">{t("market.suggestions.fixProblems", { count: problems.size })}</Alert>
          ) : null}
          <p className="text-xs text-muted-foreground">{t("market.suggestions.createHint")}</p>
        </Card>
      ) : null}

      {result ? <CreatedOrders result={result} /> : null}
    </>
  );
}

function SuggestionRow({
  item,
  checked,
  quantity,
  problem,
  canOrder,
  onToggle,
  onQuantity,
}: {
  item: MarketOrderSuggestionItem;
  checked: boolean;
  quantity: string;
  problem: "blocked" | "quantity" | null;
  canOrder: boolean;
  onToggle: (checked: boolean) => void;
  onQuantity: (text: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const blocker = suggestionBlocker(item);
  const errorId = `suggestion-${item.productId}-error`;
  const blockerId = `suggestion-${item.productId}-blocker`;
  return (
    <tr className="border-t align-top" data-testid="suggestion-row">
      <td className="px-3 py-2">
        {canOrder ? (
          <Checkbox
            checked={checked}
            disabled={blocker !== null}
            aria-label={t("market.suggestions.selectProduct", { product: item.productName })}
            aria-describedby={blocker ? blockerId : undefined}
            onChange={(event) => onToggle(event.target.checked)}
          />
        ) : null}
      </td>
      <td className="min-w-52 px-3 py-2">
        <span className="grid gap-0.5">
          <Link
            to="/market/stok/$productId"
            params={{ productId: item.productId }}
            className="font-medium underline underline-offset-4"
          >
            {item.productName}
          </Link>
          <span className="font-mono text-xs text-muted-foreground">
            {item.productCode}
            {item.categoryName ? ` · ${item.categoryName}` : ""}
          </span>
        </span>
        {item.reason === "BELOW_MIN_AT_ARRIVAL" ? (
          <StatusBadge tone="warning" className="mt-1">
            {t("market.suggestions.reason.BELOW_MIN_AT_ARRIVAL")}
          </StatusBadge>
        ) : item.reason === "NO_LIMITS" ? (
          <StatusBadge tone="neutral" className="mt-1">
            {t("market.suggestions.reason.NO_LIMITS")}
          </StatusBadge>
        ) : null}
      </td>
      <td className="px-3 py-2 text-right tabular whitespace-nowrap">
        {formatStockQuantity(item.onHand, item.unit)}
      </td>
      <td className="px-3 py-2 text-right tabular">{formatStockQuantity(item.openOrderQuantity)}</td>
      <td className="px-3 py-2 text-right tabular text-muted-foreground">
        {formatStockQuantity(item.pendingOrderQuantity)}
      </td>
      <td className="px-3 py-2 text-right tabular">{formatStockQuantity(item.dailySales)}</td>
      <td className="px-3 py-2 text-right">
        <span className="grid justify-items-end gap-0.5">
          <span className="tabular">{formatStockQuantity(item.projectedAtArrival)}</span>
          <span className="text-xs text-muted-foreground">
            {t("market.suggestions.leadTime", { count: item.leadTimeDays })}
          </span>
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular whitespace-nowrap">
        {formatStockQuantity(item.minStock)} / {formatStockQuantity(item.maxStock)}
      </td>
      <td className="px-3 py-2 text-right">
        {canOrder ? (
          <Input
            inputMode="decimal"
            className="ms-auto w-24 text-right tabular"
            aria-label={t("market.suggestions.quantityFor", { product: item.productName })}
            aria-invalid={problem === "quantity"}
            aria-describedby={problem === "quantity" ? errorId : undefined}
            disabled={blocker !== null}
            value={quantity}
            onChange={(event) => onQuantity(event.target.value)}
          />
        ) : (
          <span className="tabular">{formatStockQuantity(item.suggestedQuantity)}</span>
        )}
        {problem === "quantity" ? (
          <p id={errorId} className="mt-1 text-xs text-destructive">
            {t("market.suggestions.quantityProblem")}
          </p>
        ) : null}
      </td>
      <td className="px-3 py-2 text-right tabular">
        {item.packSize === null ? "—" : formatStockQuantity(item.packSize)}
      </td>
      <td className="min-w-44 px-3 py-2">
        {blocker ? (
          <p id={blockerId} className="max-w-56 text-xs text-warning">
            {t(`market.suggestions.blocker.${blocker}`)}
          </p>
        ) : (
          <span className="grid gap-0.5">
            <span>{item.supplierName}</span>
            <span className="text-xs text-muted-foreground">{formatUnitPrice(item.unitPrice)}</span>
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {/* Önerisi sıfır satırda "0,00 ₺" yanıltır (tutar hesaplanmadı): tire. */}
        {Decimal.from(item.suggestedQuantity).isZero() ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <MoneyText value={item.estimatedAmount} />
        )}
      </td>
    </tr>
  );
}

function CreatedOrders({
  result,
}: {
  result: {
    orders: PurchaseOrder[];
    skipped: Array<{ productId: string; productName: string; reason: string }>;
  };
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Card className="grid gap-3 p-4" aria-live="polite">
      <h2 className="text-base font-semibold">{t("market.suggestions.resultTitle")}</h2>
      {result.orders.length > 0 ? (
        <ul className="grid gap-2" aria-label={t("market.suggestions.resultTitle")}>
          {result.orders.map((order) => (
            <li key={order.id} className="flex flex-wrap items-center gap-2 text-sm">
              <Link
                to="/market/siparisler/$id"
                params={{ id: order.id }}
                className="font-mono font-medium underline underline-offset-4"
              >
                {order.orderNo}
              </Link>
              <PurchaseOrderStatusBadge status={order.status} />
              <span>{order.supplierName}</span>
              <span className="text-muted-foreground">
                {t("market.suggestions.supplierLines", { count: order.lineCount })}
              </span>
              <MoneyText value={order.netAmount} className="font-medium" />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">{t("market.suggestions.nothingCreated")}</p>
      )}
      {result.skipped.length > 0 ? (
        <Alert tone="warning" title={t("market.suggestions.skippedTitle", { count: result.skipped.length })}>
          <ul className="mt-1 grid gap-1">
            {result.skipped.map((item) => (
              <li key={item.productId}>
                {item.productName}: {item.reason}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
      <p className="text-xs text-muted-foreground">{t("market.suggestions.resultHint")}</p>
    </Card>
  );
}
